import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  ApprovedSP,
  BestFitRecommendation,
  BoardMembership,
  CareerAspiration,
  CareerJourney,
  ContractDetails,
  EDGEView,
  Experiences,
  MobilityPlan,
  OtherContribution,
  Qualification,
  SpokenLanguage,
} from '..';
import OverAllProfileUser from '../../pages/TalentProfilePage/components/OverAllProfileUser/OverAllProfileUser';
import { profileSelector } from '../../pages/TalentProfilePage/selector';
import AssessmentPrint from './components/AssessmentPrint/AssessmentPrint';
import DevelopmentPlanPrint from './components/DevelopmentPlanPrint/DevelopmentPlanPrint';
import HistoricalTalentStatusPrint from './components/HistoricalTalentStatusPrint/HistoricalTalentStatusPrint';
import OtherExperiencePrint from './components/OtherExperiencePrint/OtherExperiencePrint';
import PersonalityPrint from './components/PersonalityPrint/PersonalityPrint';
import styles from './talent-profile-print-preview.module.scss';
import TalentProfilePrintSelection from './components/TalentProfilePrintSelection/TalentProfilePrintSelection';
import { PRE_PRINT_FETCHED_SECTIONS, TP_PRINT_SECTION_NAME } from './constants';
import WorkPreference from '../WorkPreference/WorkPreference';
import NotesPrint from './components/NotesPrint/NotesPrint';
import TalentReviewPrint from './components/TalentReviewPrint/TalentReviewPrint';
import KeyStrengthDevelopmentAreaPrint from './components/KeyStrengthDevelopmentAreaPrint/KeyStrengthDevelopmentAreaPrint';
import ExternalAssessments from '../../pages/TalentProfilePage/components/ExternalAssessments/ExternalAssessments';
import ExternalExperience from '../ExternalExperience/ExternalExperience';
import TechnicalCompetencies from '../TechnicalCompetencies/TechnicalCompetencies';
import { BasicButton } from '../../assets/common';
import ReactToPrint from 'react-to-print';
import { intersectionWith, isEmpty, isEqual } from 'lodash';
import AreaOfInterest from '../AreaOfInterest/AreaOfInterest';
import CareerPreference from '../CareerPreference/CareerPreference';

const TalentProfilePrintPreview = (props) => {
  const { tokenTalentProfile, loadingSummary, loadingPersonality } = props;
  const { id } = useParams();
  const { firstSectionProfile, loading: loadingFirstSection } = useSelector(profileSelector);
  const customFileName = `${firstSectionProfile.staffId ?? id}_${
    firstSectionProfile.birthName ? firstSectionProfile.birthName.split(' ').join('_') : ''
  }.pdf`;
  const { selectedValues } = useSelector((state) => state.app.talentProfile.printProfile.printSettings);
  const printRef = useRef();
  const isRefReady = useCallback((node) => {
    if (node !== null) {
      return node;
    }
  }, []);
  const { fetchedData, othersLoading, printSettings } = useSelector((state) => state.app.talentProfile.printProfile);

  useEffect(() => {
    if (!printRef.current) return;

    const updatePrintSections = () => {
      const printSectionsDOM = printRef.current.querySelectorAll('[data-print-section]');
      printSectionsDOM.forEach((element) => {
        const printSection = element.getAttribute('data-print-section');
        if (!selectedValues.includes(printSection)) {
          element.style.display = 'none';
        } else {
          element.style.removeProperty('display');
        }
      });
    };

    updatePrintSections();

    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          updatePrintSections();
        }
      }
    });

    observer.observe(printRef.current, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [selectedValues]);

  const isLoadingAll = useMemo(() => {
    const result = false;
    const currentFetchingSection = intersectionWith(PRE_PRINT_FETCHED_SECTIONS, selectedValues, isEqual);
    for (let i = 0; i < currentFetchingSection.length; i++) {
      const section = currentFetchingSection[i];
      if (!fetchedData[section] === undefined) return true;
    }
    const leadershipLoading = othersLoading.leadership && selectedValues.includes(TP_PRINT_SECTION_NAME.LEADERSHIP_COMPETENCIES);
    const externalAssessmentLoading = othersLoading.externalAssessment && printSettings.radioOptions.component;
    const mobilityPlanLoading = othersLoading.mobilityPlan && selectedValues.includes(TP_PRINT_SECTION_NAME.MOBILITY_PLAN);

    return (
      result ||
      loadingFirstSection ||
      loadingSummary ||
      loadingPersonality ||
      othersLoading.approvedSp ||
      leadershipLoading ||
      externalAssessmentLoading ||
      mobilityPlanLoading
    );
  }, [
    fetchedData,
    selectedValues,
    loadingFirstSection,
    loadingPersonality,
    loadingSummary,
    othersLoading.approvedSp,
    othersLoading.leadership,
    othersLoading.externalAssessment,
    othersLoading.mobilityPlan,
    printSettings.radioOptions.component,
  ]);

  return (
    <Modal
      destroy
      onHide={() => props?.closeModal()}
      dialogClassName={styles.containerModal}
      show={props?.isPrinting}
      style={{ overflowX: 'auto' }}
      centered
    >
      <Modal.Body>
        <div className="relative">
          <span className={styles.settingIcon}>
            <TalentProfilePrintSelection />
          </span>
        </div>
        <div ref={printRef} className={styles.printContainer}>
          <div className={styles.distance}>
            <OverAllProfileUser isPrintPdf={true} tokenTalentProfile={tokenTalentProfile} />
          </div>
          <div className={styles.distance}>
            <CareerJourney
              isPrintTP
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.CAREER_JOURNEY]}
              id={id}
              roleUser={props?.roleUser}
              setCareerJourneyData={() => {}}
              noDetail={false}
              customStyle={{}}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <CareerAspiration isPrinting carreerAspiration={props?.carreerAspiration} tokenTalentProfile={tokenTalentProfile} />
          </div>
          <div className={styles.distance}>
            <KeyStrengthDevelopmentAreaPrint fetchedData={fetchedData[TP_PRINT_SECTION_NAME.KEY_STRENGTHS]} tokenTalentProfile={tokenTalentProfile} />
          </div>
          <div className={styles.distance}>
            <PersonalityPrint personality={props?.personality} tokenTalentProfile={tokenTalentProfile} />
          </div>
          <div className={styles.distance}>
            <BoardMembership langAndBoard={props?.langAndBoard} tokenTalentProfile={tokenTalentProfile} />
          </div>
          <div className={styles.distance}>
            <Qualification qualification={props?.qualification} tokenTalentProfile={tokenTalentProfile} />
          </div>
          <div className={styles.distance}>
            <ApprovedSP isPrintTP id={id} roleUser={props?.roleUser} tokenTalentProfile={tokenTalentProfile} />
          </div>
          <div className={styles.distance}>
            <SpokenLanguage langAndBoard={props?.lang} tokenTalentProfile={tokenTalentProfile} />
          </div>
          <div className={styles.distance}>
            <EDGEView isPrintTP id={id} roleUser={props?.roleUser} tokenTalentProfile={tokenTalentProfile} />
          </div>
          <div className={styles.distance}>
            <TechnicalCompetencies
              isPrintTP
              profileId={id}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.TECHNICAL_COMPETENCY]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>

          <div className={styles.distance}>
            <Experiences
              profileId={id}
              roleUser={props?.roleUser}
              isPrinting={true}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.EXPERIENCE]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <ExternalExperience
              staffId={id}
              role={props?.roleUser}
              isPrinting={true}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.EXTERNAL_EXPERIENCE]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <OtherExperiencePrint
              profileId={id}
              roleUser={props?.roleUser}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.OTHER_EXPERIENCE]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <OtherContribution
              profileId={id}
              isPrintPdf={true}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.OTHER_CONTRIBUTION]}
              fetchedDataProjectInvolvement={fetchedData[TP_PRINT_SECTION_NAME.PROJECT_INVOLVEMENT]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <ExternalAssessments isPrintTP staffId={id} tokenTalentProfile={tokenTalentProfile} />
          </div>
          <div className={styles.distance}>
            <DevelopmentPlanPrint
              profileId={id}
              roleUser={props?.roleUser}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.DEVELOPMENT_PLAN]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <MobilityPlan profileId={id} roleUser={props?.roleUser} isPrintPdf={true} />
          </div>
          <div className={styles.distance}>
            <ContractDetails
              profileId={id}
              roleUser={props?.roleUser}
              isPrintPdf={true}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.CONTRACT_DETAIL]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <AssessmentPrint
              profileId={id}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.ASSESSMENT_DATA]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={`${styles.container}  mt-3`}>
            <HistoricalTalentStatusPrint
              profileId={id}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.HISTORICAL_TALENT_STATUS]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <WorkPreference
              isPrintTP
              profileId={id}
              roleUser={props?.roleUser}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.WORK_PREFERENCE]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <AreaOfInterest
              isPrintTP
              profileId={id}
              roleUser={props?.roleUser}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.AREAS_OF_INTEREST]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <CareerPreference
              isPrintTP
              profileId={id}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.CAREER_PREFERENCES]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <BestFitRecommendation
              isPrintTP
              profileId={id}
              roleUser={props?.roleUser}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.RIGHT_FIT_RECOMMENDATION]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <NotesPrint fetchedData={fetchedData[TP_PRINT_SECTION_NAME.NOTES]} tokenTalentProfile={tokenTalentProfile} />
          </div>
          <div className={styles.distance}>
            <TalentReviewPrint fetchedData={fetchedData[TP_PRINT_SECTION_NAME.TALENT_REVIEW]} tokenTalentProfile={tokenTalentProfile} />
          </div>
        </div>
        <div className={styles.btn_wrapper} ref={isRefReady}>
          <PrintButton componentRef={printRef} fileName={customFileName} isLoadingAll={isLoadingAll} />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default memo(TalentProfilePrintPreview);

const PrintButton = (props) => {
  const { componentRef, fileName, isLoadingAll } = props;
  const [loading, setLoading] = useState(false);

  const reactToPrintTrigger = () => {
    return (
      <div className={styles.control}>
        <div className="mt-2">
          <div className="d-flex align-items-center justify-content-end">
            <BasicButton mode="teal" disabled={isLoadingAll || loading} data-testid="printBtn" className={styles.pritnBtn}>
              {isLoadingAll || loading ? (
                <>
                  <span className="spinner-border" style={{ width: 18, height: 18, marginRight: 5 }} /> Loading
                </>
              ) : (
                <span>Print</span>
              )}
            </BasicButton>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <ReactToPrint
        content={() => {
          const cloneDom = componentRef.current.cloneNode(true);
          const elements = cloneDom.querySelectorAll('.box-card');
          if (!isEmpty(elements)) {
            elements.forEach((el) => {
              el.style.removeProperty('width');
            });
          }
          return cloneDom;
        }}
        removeAfterPrint
        documentTitle={fileName}
        onBeforeGetContent={() => {
          setLoading(true);
        }}
        onBeforePrint={() => setLoading(false)}
        trigger={reactToPrintTrigger}
      />
    </div>
  );
};
